"use client";

import { useCallback } from "react";
import { useTranslations } from "use-intl";

import { CatalogListActionsView } from "@/components/lists/CatalogListActionsView";
import { catalogTargetListMembership } from "@/components/lists/catalogTargetListMembership";
import { catalogTargetWishlistState } from "@/components/lists/catalogTargetWishlistState";
import { useReleaseWishlistToggle } from "@/components/lists/useReleaseWishlistToggle";
import { useLists } from "@/contexts/lists-context";
import { useHydrated } from "@/hooks/use-hydrated";
import { useTrackingScope } from "@/hooks/use-tracking-scope";
import { itemQueryForAddToListsTarget, type AddToListsTarget } from "@/lib/add-to-lists-target";

interface CatalogListActionsProps {
  target: AddToListsTarget;
}

export function CatalogListActions({ target }: CatalogListActionsProps) {
  const t = useTranslations("globals.lists");
  const { allLists, getListIdsForRelease, isInCollection, isInWishlist, openAddToLists } =
    useLists();
  const hydrated = useHydrated();
  const trackingScope = useTrackingScope();
  const item = itemQueryForAddToListsTarget(target);
  const collectionList = allLists.find((list) => list.type === "collection");
  const wishlistList = allLists.find((list) => list.type === "wishlist");
  const inCollection = catalogTargetListMembership({
    fallbackMembership: isInCollection(item),
    getListIdsForRelease,
    hydrated,
    listId: collectionList?.id,
    target,
  });
  const wishlistState = catalogTargetWishlistState({
    fallbackMembership: isInWishlist(item),
    getListIdsForRelease,
    hydrated,
    listId: wishlistList?.id,
    target,
  });
  const inWishlist = wishlistState !== "none";
  const exactReleaseInWishlist = wishlistState === "exact-release";
  const collectionLabel = inCollection
    ? t("inCollection")
    : t("collectionAction", { entityType: target.entityType });
  const wishlistLabel = inWishlist
    ? t("inWishlist")
    : t("wishlistAction", { entityType: target.entityType });
  const { isPending: wishlistPending, toggle: toggleReleaseWishlist } = useReleaseWishlistToggle({
    wishlistListId: wishlistList?.id,
    workId: target.workId,
  });

  const openPreferredList = useCallback(
    (preferredListType: "collection" | "wishlist", event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      openAddToLists({ target, preferredListType, trackingScope });
    },
    [openAddToLists, target, trackingScope],
  );

  const toggleExactReleaseWishlist = useCallback(() => {
    if (target.entityType !== "release") return;

    toggleReleaseWishlist({ inWishlist: exactReleaseInWishlist, releaseId: target.releaseId });
  }, [exactReleaseInWishlist, target, toggleReleaseWishlist]);

  const openListOverview = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      openAddToLists({ target, trackingScope });
    },
    [openAddToLists, target, trackingScope],
  );

  const handleWishlistAction = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (target.entityType === "work") {
        openPreferredList("wishlist", event);
        return;
      }
      if (wishlistState === "related-release") {
        openListOverview(event);
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      toggleExactReleaseWishlist();
    },
    [openListOverview, openPreferredList, target, toggleExactReleaseWishlist, wishlistState],
  );

  const wishlistRemoveConfirmation =
    target.entityType === "release" && exactReleaseInWishlist
      ? {
          cancelLabel: t("cancel"),
          confirmLabel: t("removeItem"),
          description: t("confirmRemoveItem"),
          onConfirm: toggleExactReleaseWishlist,
          title: t("removeFromWishlist"),
        }
      : undefined;

  return (
    <CatalogListActionsView
      collectionLabel={collectionLabel}
      collectionShortLabel={t("collectionActionShort")}
      inCollection={inCollection}
      inWishlist={inWishlist}
      onCollectionClick={(event) => openPreferredList("collection", event)}
      onWishlistClick={handleWishlistAction}
      wishlistLabel={wishlistLabel}
      wishlistPending={target.entityType === "release" && wishlistPending}
      wishlistRemoveConfirmation={wishlistRemoveConfirmation}
      wishlistShortLabel={inWishlist ? t("removeItem") : t("wishlistActionShort")}
    />
  );
}
